// /web/src/pages/Dashboard.jsx
import React from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { useApi } from "../lib/api";

const isUUID = (s) =>
  typeof s === "string" &&
  /^[0-9a-fA-F-]{8}-[0-9a-fA-F-]{4}-[1-5][0-9a-fA-F-]{3}-[89abAB][0-9a-fA-F-]{3}-[0-9a-fA-F-]{12}$/.test(s);

const PALETTE = [
  "#265c80",
  "#3274a4",
  "#479fa1",
  "#64a56b",
  "#9cb653",
  "#dfc650",
  "#e9a945",
  "#df6c3d",
  "#cf423b",
  "#b8375d",
  "#833f78",
  "#594d95",
];

function parseTimestamp(value) {
  if (!value) return 0;
  const str = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(str)) {
    const iso = str.replace(" ", "T");
    return Date.parse(iso.endsWith("Z") ? iso : `${iso}Z`);
  }
  const parsed = Date.parse(str);
  return Number.isFinite(parsed) ? parsed : 0;
}

function SkeletonCard() {
  return (
    <div className="pd-project-card" aria-hidden>
      <div className="pd-skeleton-row" style={{ width: "30%" }} />
      <div className="pd-skeleton-row" style={{ width: "70%" }} />
      <div className="pd-skeleton-row" style={{ width: "90%" }} />
      <div className="pd-skeleton-row" style={{ width: "65%" }} />
      <div className="pd-skeleton-row" style={{ width: "45%" }} />
    </div>
  );
}

function ProjectCard({ project, accent, onDelete, onDuplicate }) {
  const navigate = useNavigate();
  const colour = accent || "#25374f";

  const handleOpen = (event) => {
    event.preventDefault();
    if (!isUUID(project?.id)) return;
    navigate(`/projects/${project.id}`);
  };

  const handleDelete = (event) => {
    event.preventDefault();
    if (onDelete) onDelete(project.id, project.name);
  };

  const handleDuplicate = (event) => {
    event.preventDefault();
    if (onDuplicate) onDuplicate(project);
  };

  const type = (project?.building_use || "Project").toString();

  return (
    <article className="pd-project-card">
      <div className="pd-card-top">
        <span className={`pd-type ${type.toLowerCase()}`}>{type}</span>
        <div className="pd-card-menu">
          <button type="button" className="pd-menu-btn" aria-haspopup="menu" aria-label="Project options">
            ⋮
          </button>
          <div className="pd-menu" role="menu">
            <button type="button" onClick={handleDuplicate} role="menuitem">
              Duplicate
            </button>
            <button type="button" onClick={handleDelete} role="menuitem">
              Delete
            </button>
          </div>
        </div>
      </div>

      <h3 style={{ borderLeft: `4px solid ${colour}`, paddingLeft: 12 }}>{project?.name || "Untitled project"}</h3>

      <ul className="pd-details">
        <li>Postcode: {project?.postcode || "—"}</li>
        <li>Local Authority: {project?.local_authority || "—"}</li>
        <li>Number of Floors: {project?.floors_count ?? "—"}</li>
      </ul>

      <button type="button" onClick={handleOpen} className="pd-btn pd-btn--ghost small">
        Open project
      </button>
    </article>
  );
}

export default function Dashboard() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const api = useApi();
  const { refresh, searchTerm = "" } = useOutletContext() || {};

  const [projects, setProjects] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const loadedOnceRef = React.useRef(false);

  const loadDashboard = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const proj = await api("/projects");

      setProjects(Array.isArray(proj) ? proj : []);
    } catch (err) {
      const msg = err?.message || "Request failed";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [api]);

  React.useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      setLoading(false);
      setError("Please log in to view your dashboard.");
      return;
    }
    if (loadedOnceRef.current) return;
    loadedOnceRef.current = true;
    loadDashboard();
  }, [authLoading, isAuthenticated, loadDashboard]);

  React.useEffect(() => {
    if (!refresh) return;
    refresh();
  }, [refresh]);

  const handleDelete = React.useCallback(
    async (id, name) => {
      const ok = window.confirm(`Delete “${name || "Untitled"}”? This cannot be undone.`);
      if (!ok) return;
      try {
        if (!isUUID(id)) throw new Error("Invalid project id");
        await api(`/projects/${id}`, { method: "DELETE" });
        setProjects((prev) => prev.filter((project) => project.id !== id));
      } catch (err) {
        console.error(err);
        window.alert("Sorry — couldn’t delete that project.");
      }
    },
    [api]
  );

  const handleDuplicate = React.useCallback(
    async (project) => {
      if (!project || !isUUID(project.id)) {
        window.alert("Sorry — couldn’t duplicate that project.");
        return;
      }

      const base = (project.name && project.name.trim()) || "Untitled project";
      const suggestion = base.toLowerCase().includes("copy") ? base : `${base} copy`;
      const name = window.prompt(
        "Duplicate project\n\nEnter a name for the duplicated project:",
        suggestion
      );
      if (name === null) return;

      const trimmed = name.trim();
      if (!trimmed) {
        window.alert("Please enter a name for the duplicated project.");
        return;
      }

      try {
        const newProject = await api(`/projects/${project.id}/duplicate`, {
          method: "POST",
          body: { name: trimmed },
        });
        setProjects((prev) => [newProject, ...prev.filter((p) => p.id !== newProject.id)]);
      } catch (err) {
        console.error(err);
        window.alert("Sorry — couldn’t duplicate that project.");
      }
    },
    [api]
  );

  const orderedProjects = React.useMemo(() => {
    return [...projects].sort((a, b) => parseTimestamp(b.updated_at) - parseTimestamp(a.updated_at));
  }, [projects]);

  const filteredProjects = React.useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return orderedProjects;
    return orderedProjects.filter((project) => {
      const name = (project?.name || "Untitled project").toLowerCase();
      return name.includes(query);
    });
  }, [orderedProjects, searchTerm]);

  return (
    <div className="pd-dashboard-page">
      <section>
        <div className="pd-projects__head">
          <div>
            <h1>Projects</h1>
          </div>
          <div className="pd-projects__actions">
            <Link to="/projects/new" className="pd-btn pd-btn--primary">
              Add project +
            </Link>
          </div>
        </div>

        {error && <div className="pd-alert">{error}</div>}

        {loading ? (
          <div className="pd-project-grid">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : orderedProjects.length === 0 ? (
          <div className="pd-empty-state" role="region" aria-live="polite">
            <div className="pd-empty-state__card">
              <p className="pd-empty-state__eyebrow">Welcome to Pebbledash</p>
              <h2>Let’s create your first project</h2>
              <p className="pd-empty-state__body">
                Capture your building’s location, fabric and systems so you can explore retrofit
                options, costs and savings with confidence.
              </p>
              <div className="pd-empty-state__actions">
                <Link to="/projects/new" className="pd-btn pd-btn--primary">
                  Create a project
                </Link>
              </div>
            </div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="pd-alert" style={{ background: "rgba(37,55,79,0.08)", color: "#111", borderColor: "transparent" }}>
            No projects match “{searchTerm.trim()}”.
          </div>
        ) : (
          <div className="pd-project-grid">
            {filteredProjects.map((project, index) => (
              <ProjectCard
                key={project.id || index}
                project={project}
                accent={PALETTE[index % PALETTE.length]}
                onDelete={handleDelete}
                onDuplicate={handleDuplicate}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

